import {
  Card,
  Button,
  Input,
  Space,
  Typography,
  Select,
  Drawer,
  Form,
  Popconfirm,
  Upload,
  Image,
  message,
  Flex,
  Avatar,
  Divider,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { useEffect, useState } from "react";
import { db_fire, storage } from "../firebase";

const { Text } = Typography;
const { Option } = Select;

/* 🔥 STATUS OPTIONS */
const STATUS_OPTIONS = [
  { label: "ON", value: "ON" },
  { label: "Unavailable", value: "Unavailable" },
  { label: "Out of Stock", value: "Out of Stock" },
  { label: "Maintenance", value: "Maintenance" },
];

export default function Item() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  /* 🔥 REALTIME FETCH */
  useEffect(() => {
    const unsub = onSnapshot(collection(db_fire, "menu_items"), (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  /* 📸 IMAGE UPLOAD */
  const uploadImage = async (file) => {
    try {
      setUploading(true);
      const imageRef = ref(
        storage,
        `menu_items/${Date.now()}_${file.name}`
      );

      await uploadBytes(imageRef, file);
      const url = await getDownloadURL(imageRef);

      setImageUrl(url);
      message.success("Image uploaded successfully");
    } catch (err) {
      message.error("Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  /* ➕ / ✏️ SAVE ITEM */
  const saveItem = async (values) => {
    const isOn = values.status === "ON";

    const payload = {
      title: values.title,
      price: Number(values.price),
      category: values.category,
      type: values.type,
      disabled: !isOn,
      remark: isOn ? "" : values.status,
      image: imageUrl || "",
      updatedAt: serverTimestamp(),
    };

    if (editingItem) {
      await updateDoc(doc(db_fire, "menu_items", editingItem.id), payload);
      message.success("Item updated");
    } else {
      await addDoc(collection(db_fire, "menu_items"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
      message.success("Item added");
    }

    closeDrawer();
  };

  /* ✏️ OPEN EDIT */
  const openEdit = (item) => {
    setEditingItem(item);
    setImageUrl(item.image || "");
    setOpen(true);

    form.setFieldsValue({
      title: item.title,
      price: item.price,
      category: item.category,
      type: item.type,
      status: item.disabled ? item.remark : "ON",
    });
  };

  /* ❌ CLOSE DRAWER */
  const closeDrawer = () => {
    setOpen(false);
    setEditingItem(null);
    setImageUrl("");
    form.resetFields();
  };

  /* 🗑 DELETE */
  const deleteItemHandler = async (id) => {
    await deleteDoc(doc(db_fire, "menu_items", id));
    message.success("Item deleted");
  };

  /* 🔁 STATUS CHANGE */
  const changeStatus = async (item, value) => {
    await updateDoc(doc(db_fire, "menu_items", item.id), {
      disabled: value !== "ON",
      remark: value === "ON" ? "" : value,
      updatedAt: serverTimestamp(),
    });
  };

  return (
    <>
      <Flex style={{ marginBottom: 16 }} align="center" justify="space-between">
 
        <Button size="small" type="primary" onClick={() => setOpen(true)}>
          + Add Item
        </Button>
      </Flex>

      {items.map((i) => (
        <Card key={i.id} size="small"
          style={{
            marginBottom: 10,
            filter: `grayscale(${!i.disabled ? 0 : 1})`
          }}>
          <Flex vertical gap={10}>
            <Flex gap={10}>
              <Avatar
                src={i.image || "https://via.placeholder.com/60"}
                size={60}
                style={{ borderRadius: 8 }}
              />

              <Flex vertical>
                <Text strong>₹{i.price}</Text>
                <Text>{i.title}</Text> 
              </Flex>

            </Flex>


            <Flex gap={5}>

              <Select
                size="small"
                value={i.disabled ? i.remark : "ON"}
                style={{ width: 140 }}
                onChange={(v) => changeStatus(i, v)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <Option key={s.value} value={s.value}>
                    {s.label}
                  </Option>
                ))}
              </Select>

              <Button
                style={{ width: 70 }} size="small" onClick={() => openEdit(i)}>Edit</Button>

              <Popconfirm
                title="Delete this item?"
                onConfirm={() => deleteItemHandler(i.id)}
              >
                <Button
                style={{ width: 70 }} size="small" danger>Delete</Button>
              </Popconfirm>
            </Flex>

          </Flex>
        </Card>
      ))}

      {/* ➕ / ✏️ ADD / EDIT DRAWER */}
      <Drawer
        title={editingItem ? "Edit Item" : "Add Item"}
        open={open}
        onClose={closeDrawer}
        width={380}
      >
        <Form layout="vertical" form={form} onFinish={saveItem}>
          <Form.Item
            label="Item Name"
            name="title"
            rules={[{ required: true, message: "Item name required" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Price"
            name="price"
            rules={[{ required: true, message: "Price required" }]}
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item label="Category" name="category" initialValue="Biryani">
            <Input />
          </Form.Item>

          <Form.Item label="Type" name="type" initialValue="Veg">
            <Select>
              <Option value="Veg">Veg</Option>
              <Option value="Non-Veg">Non-Veg</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            initialValue="ON"
            rules={[{ required: true }]}
          >
            <Select>
              {STATUS_OPTIONS.map((s) => (
                <Option key={s.value} value={s.value}>
                  {s.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Item Image">
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                uploadImage(file);
                return false;
              }}
            >
              <Button
                icon={<UploadOutlined />}
                loading={uploading}
                block
              >
                Upload Image
              </Button>
            </Upload>

            {imageUrl && (
              <Image
                src={imageUrl}
                width={120}
                style={{ marginTop: 10, borderRadius: 8 }}
              />
            )}
          </Form.Item>

          <Button type="primary" htmlType="submit" block>
            {editingItem ? "Update Item" : "Add Item"}
          </Button>
        </Form>
      </Drawer>
    </>
  );
}
